import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@c2fo/liquidity';
import RadioInput from '@/enterprise/components/RadioInput';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { Schedule, SchedulingInput } from '@/enterprise/features/offerModal/inputs/SchedulingInput';
import { Cadence } from '@/enterprise/features/offerModal/types';
import { OfferBuilderFrequency } from './OfferBuilderContext';
import { hasAllMarketsWithZeroEligibleAmount } from './utils';

export interface FrequencyInputProps {
  frequency: OfferBuilderFrequency;
  schedule: Schedule;
  hasExistingSchedule: boolean;
  onFrequencyChange: (frequency: OfferBuilderFrequency) => void;
  onScheduleChange: (schedule: Schedule) => void;
  errors?: {
    cadence?: {
      message: string;
    };
  };
  includeDescription?: boolean;
  takerMarkets?: TakerMarket[];
}

const FrequencyInput = ({
  frequency,
  schedule,
  hasExistingSchedule,
  onFrequencyChange,
  onScheduleChange,
  errors = {},
  includeDescription = true,
  takerMarkets = [],
}: FrequencyInputProps) => {
  const { t } = useTranslation();
  const [enableSchedule, setEnableSchedule] = useState(hasExistingSchedule);

  const isOneTimeDisabled = hasAllMarketsWithZeroEligibleAmount(takerMarkets);

  const handleFrequencyChange = (newFrequency: OfferBuilderFrequency) => {
    onFrequencyChange(newFrequency);
  };

  const setStartDate = (date: string | undefined) => {
    onScheduleChange({ ...schedule, startDate: date });
  };

  const setEndDate = (date: string | undefined) => {
    onScheduleChange({ ...schedule, endDate: date });
  };

  const setCadence = (cadence: Cadence) => {
    onScheduleChange({ ...schedule, cadence });
  };

  return (
    <div className="space-y-3 p-4">
      {includeDescription && <p className="text-sm">{t('scheduling.frequencyDescription')}</p>}
      <div className="space-y-2">
        <div>
          <RadioInput
            value="one-time"
            label={`${t('builder.oneTime')}${isOneTimeDisabled ? '*' : ''}`}
            description={t('scheduling.oneTimeDescription')}
            checked={frequency === 'oneTime'}
            onChange={() => handleFrequencyChange('oneTime')}
            variant="light"
            disabled={isOneTimeDisabled}
          />
          {isOneTimeDisabled && (
            <p className="py-1 pl-2 text-xs font-medium text-text-secondary">
              *{t('builder.oneTimeDisabledForFutureOffers')}
            </p>
          )}
        </div>
        <div>
          <RadioInput
            value="recurring"
            label={t('builder.recurring')}
            description={t('scheduling.recurringDescription')}
            checked={frequency === 'recurring'}
            onChange={() => handleFrequencyChange('recurring')}
            className={frequency === 'recurring' ? 'rounded-b-none border-b-0' : ''}
            variant="light"
          />
          {frequency === 'recurring' && (
            <div className="rounded-b border border-t-0 border-primary-500 bg-white pt-0">
              <div className="pb-4 pl-11">
                <Checkbox
                  id="enable-schedule"
                  checked={enableSchedule}
                  onChange={(e) => setEnableSchedule(e.target.checked)}
                  label={t('scheduling.enableSchedule')}
                />
              </div>
              {enableSchedule && (
                <>
                  <div className="px-4">
                    <div className="h-[1px] w-full bg-stroke" />
                  </div>
                  <div className="space-y-4 pl-7">
                    <SchedulingInput
                      takerMarkets={takerMarkets}
                      schedule={schedule}
                      setStartDate={setStartDate}
                      setEndDate={setEndDate}
                      setCadence={setCadence}
                      errors={errors}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FrequencyInput;
